import React, { useRef } from "react";
import { Box, Flex, Grid, Textarea } from "theme-ui";

import H3 from "components/typography/H3";
import H5 from "components/typography/H5";
import Separator from "components/ui/Separator";
import Subtext from "components/typography/Subtext";

// Company Terms feature (Session 31) — mirrors GeneralSettings' visual
// pattern (H5 label / Subtext description / field). Terms text lives as
// `company_data` rows keyed on quoteTerms / poTerms / invoiceTerms.
// The parent Formik in edit.tsx already loads these via the flattened
// `data` object; submitUpdate whitelists them so Save persists.

// Kept in sync with backend DEFAULT_TERMS in
// src/modules/shared/pdf-layout.helpers.ts so admins see exactly what
// they'd revert to when they hit "Reset to default".
const DEFAULT_TERMS_PLACEHOLDERS: { quoteTerms: string; poTerms: string; invoiceTerms: string } = {
  quoteTerms:
    "The actual date of service will be determined at a later date, after the acceptance of this quote. The price includes delivery fees which are estimated and may change once the order is placed. GetMulch.com contracts the supply and delivery of mulch through a local 3rd party verified partner. Installation and delivery truck operators are not responsible for any damage caused by their equipment to the customer's property. GetMulch.com is not responsible for any injury to persons or property damage caused by installation and delivery truck operators. Customer acknowledges and accepts these provisions with the acceptance of this quote. If you would like to discuss this quote in more detail before you accept or if you have any questions just give us a call at 1 (888) 816-8524.",
  poTerms:
    "The unit price per yard listed in this Purchase Order is established as fair and reflective of prevailing market conditions in your service area, based on current industry benchmarks and past performance. By fulfilling this order, you acknowledge and accept the pricing and terms herein. If you believe the listed price or any part of this Purchase Order is inaccurate or requires clarification, you are required to notify our Operations team prior to scheduling or performing any services. You may reach us at (407) 574-6640 or via email at support@getmulch.com. Failure to dispute pricing or terms prior to acceptance and execution will be interpreted as full agreement. All deliveries and installations are conducted through our vetted third-party vendor network. Vendors assume responsibility for coordinating service delivery in a timely and professional manner in accordance with GetMulch.com standards.",
  invoiceTerms:
    "Please contact https://getmulch.com at (407) 574-6640 or email orders@getmulch.com with any questions concerning this invoice.",
};

type Props = {
  values: any;
  setFieldValue: (i: string, v: any) => void;
};

// Session 61 - merge tags in the COMPANY-DEFAULT terms, for parity with
// the per-record TermsEditor (WO Quote/Invoice/PO tabs). Same tag set;
// they resolve at render via resolveTerms()/applyTermTokens on the
// backend, so a default like "Net {{net_days}}" stays live per record.
// Kept identical to MERGE_TAGS in components/TermsEditor.
const MERGE_TAGS = ["{{net_days}}", "{{due_date}}", "{{payment_terms}}", "{{fuel_surcharge_notice}}"];
// {{fuel_surcharge_notice}} only resolves on quotes (it is the "may apply"
// notice the client accepts); on a PO or invoice the line itself is the
// disclosure, so the chip is offered on Quote Terms only (2026-10-08).
const tagsFor = (fieldKey: string) =>
  fieldKey === "quoteTerms" ? MERGE_TAGS : MERGE_TAGS.filter((t) => t !== "{{fuel_surcharge_notice}}");

function TermsSection({
  title,
  description,
  fieldKey,
  values,
  setFieldValue,
}: Props & { title: string; description: string; fieldKey: keyof typeof DEFAULT_TERMS_PLACEHOLDERS }) {
  const current: string = values?.data?.[fieldKey] ?? "";
  const placeholder = DEFAULT_TERMS_PLACEHOLDERS[fieldKey];
  const isOverriding = typeof current === "string" && current.trim().length > 0;

  // Merge-tag inserter (parity with the per-record TermsEditor). Drops a
  // token at the cursor, writing through Formik's setFieldValue. Each
  // TermsSection instance has its own ref, so the three sections don't
  // cross-insert.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const insertTag = (tag: string) => {
    const el = textareaRef.current;
    if (!el) {
      setFieldValue(`data.${fieldKey}`, current + tag);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + tag + current.slice(end);
    setFieldValue(`data.${fieldKey}`, next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <>
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            {title}
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            {description}
          </Subtext>
          <Box sx={{ marginTop: "8px" }}>
            <Subtext marginTop={0} marginBottom={0} fontSize={"11px"} fontWeight={"400"} fontFamily={"Poppins"} color={isOverriding ? "#000" : "#888"}>
              {isOverriding ? "Custom text applied" : "Using default text"}
            </Subtext>
            {isOverriding && (
              <button
                type="button"
                onClick={() => setFieldValue(`data.${fieldKey}`, "")}
                style={{
                  marginTop: "4px",
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#4285f4",
                  cursor: "pointer",
                  fontFamily: "Poppins",
                  fontSize: "11px",
                  textDecoration: "underline",
                }}
              >
                Reset to default
              </button>
            )}
          </Box>
        </Box>
        <Flex sx={{ gap: "1rem", flexDirection: "column" }}>
          <Textarea
            ref={textareaRef}
            name={`data[${fieldKey}]`}
            value={current}
            placeholder={placeholder}
            onChange={(e: any) => setFieldValue(`data.${fieldKey}`, e.target.value)}
            rows={8}
            sx={{
              fontFamily: "Poppins",
              fontSize: "13px",
              lineHeight: "1.5",
              resize: "vertical",
            }}
          />
          {/* Merge-tag inserter - matches the per-record editor. */}
          <Box>
            <Subtext marginTop={0} marginBottom={"6px"} fontSize={"11px"} fontWeight={"600"} fontFamily={"Poppins"} color="#185FA5">
              Insert merge tag
            </Subtext>
            <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
              {tagsFor(fieldKey).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => insertTag(tag)}
                  style={{
                    background: "#EEF4FB",
                    border: "1px solid #CFE0F2",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#185FA5",
                    cursor: "pointer",
                  }}
                >
                  {tag}
                </button>
              ))}
            </Flex>
          </Box>
          <Subtext marginTop={0} marginBottom={0} fontSize={"11px"} fontWeight={"400"} fontFamily={"Poppins"} color="#888">
            {`Leave blank to use the default. Merge tags resolve per record when the document is generated (${tagsFor(fieldKey).join(", ")}); a tag with no value for that record is left blank.`}
            {fieldKey === "quoteTerms" && " {{fuel_surcharge_notice}} adds the fuel surcharge sentence only while the surcharge is on and the client is not waived."}
          </Subtext>
        </Flex>
      </Grid>
      <Separator />
    </>
  );
}

export default function DocumentSettings({ values, setFieldValue }: Props) {
  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          Document Terms
        </H3>
      </Box>
      <Separator />

      <TermsSection
        title="Quote Terms"
        description="Legal / disclaimer text that appears at the bottom of every Quote PDF and its in-app view."
        fieldKey="quoteTerms"
        values={values}
        setFieldValue={setFieldValue}
      />

      <TermsSection
        title="Purchase Order Terms"
        description="Legal / disclaimer text that appears at the bottom of every Purchase Order sent to providers."
        fieldKey="poTerms"
        values={values}
        setFieldValue={setFieldValue}
      />

      <TermsSection
        title="Invoice Terms"
        description="Contact / payment guidance text that appears at the bottom of every Invoice sent to clients."
        fieldKey="invoiceTerms"
        values={values}
        setFieldValue={setFieldValue}
      />
    </>
  );
}
